// @ts-check
import React, { Component } from 'react';
import { browserHistory } from 'react-router';
import LoadingContent from 'shared/components/LoadingContent';
import { actions as GeographicActions } from 'agent_portal/actions/Geographic';
import api from 'utils/api';
import { initialize } from 'redux-form';
import { reduxForm } from 'utils/reduxFormWrapper';
import { connect } from 'react-redux';
import PresentationComponent from './PresentationComponent';
import buildFormData from './buildFormData';

/** @typedef {{
  initialSpecialtyId: (string|undefined),
  existingPatientId: (string|null),
  onSubmit: (formData: FormData) => any,
  dispatch: (Object) => any,
  formName: string,
  currentOrganizationId: string
}} NewReferralProfileFormPropType
*/
/** @typedef {{
  shouldUseExistingPatient: boolean,
  shouldStackLastNameOnTop: boolean,
  existingPatient: (Object|null)
}} NewReferralProfileFormStateType
*/

/** @augments { Component<NewReferralProfileFormPropType, NewReferralProfileFormStateType> } */
class NewReferralProfileForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldUseExistingPatient: !!props.existingPatientId,
      shouldStackLastNameOnTop: false,
      existingPatient: null,
    };
    this.createReferralProfile = this.createReferralProfile.bind(this);
    this.removeExistingPatient = this.removeExistingPatient.bind(this);
    this.bringLastNameForward = this.bringLastNameForward.bind(this);
    this.pushLastNameBackwards = this.pushLastNameBackwards.bind(this);
  }

  componentDidMount() {
    GeographicActions.fetchStates();

    // if we're using an existing patient, fetch the data for that patient, put in state.
    if (this.state.shouldUseExistingPatient) {
      api
        .get(
          `/agent_portal/api/referral_profiles/existing_patients/${this.props.existingPatientId}`
        )
        .then((response) => {
          this.setState({ existingPatient: response.data.existingPatient });
        });
    }
  }

  /**
   * If we get a new `existingPatientId` we want to compare it to the one we have.
   * If it's different, fetch data for new id and put in state
   * If it's different but not present, reset the form values to be empty (clear existing patient).
   */
  componentDidUpdate(prevProps) {
    if (prevProps.existingPatientId !== this.props.existingPatientId) {
      if (!!this.props.existingPatientId) {
        this.setState({
          shouldUseExistingPatient: true,
          existingPatient: null,
        });
        api
          .get(
            `/agent_portal/api/referral_profiles/existing_patients/${this.props.existingPatientId}`
          )
          .then((response) => {
            this.setState({ existingPatient: response.data.existingPatient });
          });
      } else {
        this.setState({
          shouldUseExistingPatient: false,
          existingPatient: null,
        });
        this.props.dispatch(
          initialize('referralProfile', this.getInitialValues({}))
        );
      }
    }
  }

  /**
   * Handle form submission to create a new ReferralProfile. Nav on success.
   * @param {Object} formValues
   */
  createReferralProfile(formValues) {
    // 1. Augment `formValues` with `existingPatientId` and handle enteredBySender as necessary.
    const { shouldUseExistingPatient, existingPatient } = this.state;
    const { currentOrganizationId } = this.props;

    const {
      externalReferringProvider,
      externalReferringLocationId,
      createdReferringProvider,
      createdReferringLocation,
      internalReferringProviderEmployment,
      internalReferringLocationId,
      ...values
    } = formValues;

    if (shouldUseExistingPatient) {
      values.existingPatientId = existingPatient.id;
    }

    if (values.enteredBySender) {
      values.referringProviderId =
        internalReferringProviderEmployment.providerId;
      values.referringLocationId = internalReferringLocationId;
    } else {
      const referringProvider =
        createdReferringProvider || externalReferringProvider;
      values.referringProviderId = referringProvider.id;
      values.referringLocationId = createdReferringLocation
        ? createdReferringLocation.id
        : externalReferringLocationId;
      values.networkOrganizationId = currentOrganizationId;
    }

    // TODO: Can setting org id to the inbound referring org here be a backdoor to fixing radius search related issues?

    values.networkOrganizationId =
      values.networkOrganizationId || this.props.selectedNetworkId;

    // 2. Prepare a `FormData` object for submission, including files and correct key for data
    const formData = buildFormData(values, { dataKey: 'referral_profile' });

    // 3. Pass `FormData` to `onSubmit` from parent, which will be responsible for API call + redirect.
    return this.props.onSubmit(formData);
  }

  getInitialValues(existingPatient) {
    return {
      referringOrganization: null,
      enteredBySender: true,
      externalReferringProvider: {
        id: '',
        name: '',
        score: 0,
        stateNames: [],
        specialtyNames: [],
      },
      patientCoverageAttributes: {
        insuranceCoverages: [{ unlistedPayer: false }],
      },
      specialtyId: this.props.initialSpecialtyId,
      ...existingPatient,
      fileAttachmentsAttributes: [{}],
    };
  }

  /**
   * [HACK] Update state to indicate last name field should be pushed forward with z-index
   */
  bringLastNameForward() {
    this.setState({ shouldStackLastNameOnTop: true });
  }

  /**
   * [HACK] Update state to indicate last name field should be pushed backwards with z-index
   */
  pushLastNameBackwards() {
    this.setState({ shouldStackLastNameOnTop: false });
  }

  /**
   * Remove the existing patient by navigating to this form without any url params,
   * thus clearing the exisitng patient business.
   */
  removeExistingPatient() {
    browserHistory.push('/a/portal/referral_profiles/new');
  }

  render() {
    if (this.state.shouldUseExistingPatient && !this.state.existingPatient) {
      return <LoadingContent />;
    }

    return (
      <FormifiedPresentationComponent
        form={this.props.formName}
        network={this.props.network}
        removeExistingPatient={this.removeExistingPatient}
        createReferralProfile={this.createReferralProfile}
        shouldUseExistingPatient={this.state.shouldUseExistingPatient}
        existingPatient={this.state.existingPatient}
        bringLastNameForward={this.bringLastNameForward}
        pushLastNameBackwards={this.pushLastNameBackwards}
        shouldStackLastNameOnTop={this.state.shouldStackLastNameOnTop}
        initialValues={this.getInitialValues(this.state.existingPatient)}
        inboundReferrableSpecialties={this.props.inboundReferrableSpecialties}
        referrableSpecialties={this.props.referrableSpecialties}
        specialties={this.props.specialties}
        states={this.props.states}
      />
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const selectedNetworkId = ownProps.selectedNetworkId || 'preferral';
  const selectedNetwork = state.referralWizard.availableNetworks.find(
    (network) => network.id === selectedNetworkId
  );
  // FIXME: Handle case when selectedNetwork isn't found?
  // Need to fall back to AJAX call, or alternatively, _always_ make AJAX call for this, possibly
  // combining with all the other AJAX calls on this page so we don't have 3-4 requests.
  const inboundReferrableSpecialties =
    state.referralWizard.inboundReferrableSpecialties;

  return {
    network: selectedNetwork,
    inboundReferrableSpecialties,
    referrableSpecialties: selectedNetwork.referrableSpecialties,
    states: state.geographic.statesCollection.items,
    currentOrganizationId: state.currentAgent.currentEmployment.organizationId,
  };
};

const FormifiedPresentationComponent = reduxForm()(PresentationComponent);

export default connect(mapStateToProps)(NewReferralProfileForm);
